import { FieldState, FormState } from 'formstate';
import { useWrapRequest } from 'use-wrap-request';

import {
    ActivityEventType,
    FacilityVisitEvent,
    FacilityVisitType
} from '@api/FitnessActivityClient';
import { toZonedDateTime, ZonedDateTime } from '@ms-rewards/date-lib';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Api } from '../../../../../core/api';
import { required, useModel, wrapInFormField } from '../../../../../core/forms';
import { injectTSDI } from '../../../../../core/tsdi';
import { UserContext } from '../../../../../users/user';
import { EmployeeContext } from '../../../index';
import { DEFAULT_TIME_ZONE } from '@components/utils/time';

export function useStore(onSuccess?: () => void) {
    const { fitnessActivityClient } = injectTSDI(Api);
    const employee = React.useContext(EmployeeContext);
    const user = React.useContext(UserContext);

    const addFacilityVisitRequest = useWrapRequest(
        async (data: FacilityVisitEvent) => {
            const response =
                await fitnessActivityClient.admin.activitySupportControllerCreateFacilityVisitActivity(
                    data
                );
            if (onSuccess) {
                onSuccess();
            }
            return response;
        },
        { successMessage: () => 'Facility visit added' }
    );

    const model = useModel(() => {
        const isAfterStart = (
            val: ZonedDateTime | undefined
        ): string | false => {
            if (!val) {
                return false;
            }
            const fromField = form.$.from;

            if (fromField.value && val.isBefore(fromField.value)) {
                return 'End time must be after start time';
            }

            return false;
        };
        const now = ZonedDateTime.now('Europe/Berlin');
        const form = new FormState({
            timeZone: new FieldState<string>(DEFAULT_TIME_ZONE),
            from: new FieldState<ZonedDateTime>(now).validators(required()),
            to: new FieldState<ZonedDateTime>(now).validators(
                required(),
                isAfterStart
            ),
            facilityId: new FieldState<string>('').validators(required()),
            facilityName: new FieldState<string>('').validators(required())
        });

        return {
            form,
            fields: {
                timeZone: wrapInFormField(form.$.timeZone),
                from: wrapInFormField(form.$.from),
                to: wrapInFormField(form.$.to),
                facilityId: wrapInFormField(form.$.facilityId),
                facilityName: wrapInFormField(form.$.facilityName)
            }
        };
    });

    const onSubmitHandler = async () => {
        const validation = await model.form.validate();
        const { from, to, facilityId, facilityName, timeZone } = model.fields;
        const userDay = toZonedDateTime(
            from.value,
            ZonedDateTime.getLocalTZ()
        ).format('yyyy-MM-DD');
        if (validation.hasError) {
            throw new Error(model.form.error as string);
        }
        try {
            await addFacilityVisitRequest.request([
                {
                    messageId: uuidv4().toString(),
                    userId: employee.mySportsId || user.id,
                    timestamp: toZonedDateTime(
                        from.value,
                        timeZone.value
                    ).toString(),
                    userDay: userDay,
                    type: ActivityEventType.FACILITY_VISIT,
                    facilityId: facilityId.value,
                    facilityName: facilityName.value,
                    facilityVisitType: FacilityVisitType.AUTOMATIC,
                    visitDateRange: {
                        from: toZonedDateTime(
                            from.value,
                            timeZone.value
                        ).toString(),
                        to: toZonedDateTime(to.value, timeZone.value).toString()
                    },
                    studioName: facilityName.value
                }
            ]);
        } catch (err) {
            alert(err);
        }
    };

    return {
        fields: model.fields,
        errors: model.form.error,
        onSubmitHandler,
        addFacilityVisitRequest: addFacilityVisitRequest
    };
}
